import * as THREE from 'three';
import { Dog } from './dog/Dog';
import { PrimitiveBody } from './dog/PrimitiveBody';
import { RiggedBody } from './dog/RiggedBody';
import { loadProfile, saveProfile, PROFILES } from './dog/Profile';
import { GroundPicker } from './input';
import { VoiceInput } from './voice';
import { Keyboard } from './keys';
import { Neighbourhood } from './world/Neighbourhood';
import { Person } from './world/People';
import { SkyDome } from './world/SkyDome';
import { Walker } from './walk';
import { parseCommand } from './commands';
import { DeviceMotionInput } from './motion';
import { Album } from './album';
import { Household } from './care/Household';
import { CarePanel } from './care/CarePanel';
import { FoodBowl } from './world/FoodBowl';
import { Feeding } from './world/Feeding';
import { Fetch } from './world/Fetch';
import { Yard } from './world/Yard';
import { Visitors } from './world/Visitors';
import { Tricks, emptyTricks, type TrickLevels } from './world/Tricks';
import { CareActions, type CareAction } from './world/Care';
import type { NeedsState } from './world/Care';
import { today } from './care/CareRecord';
import { unlockedCollar } from './care/Collars';
import { PhotoMode } from './photo';
import { Gauges } from './hud/Gauges';
import { Inventory } from './inventory';
import { holidayFor, daysTogether } from './world/Holidays';
import { License, AVATARS } from './care/License';
import { Account } from './care/Account';
import { Alarm } from './care/Alarm';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { PRICES } from './care/CareRecord';
import { Push } from './care/Push';
import { saveProfile as saveDogChoice } from './dog/Profile';
import { Weather, daylightOverride } from './world/Weather';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Landmarks } from './world/Landmarks';
import { Student } from './world/Student';
import { Agility } from './world/Agility';
import { Business, type BusinessState } from './world/Business';
import { Home } from './world/Home';
import { Colliders } from './world/Colliders';
import { Games } from './world/Games';

const canvas = document.querySelector<HTMLCanvasElement>('#app')!;
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // 2 is plenty on phones; 3 burns battery
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

const scene = new THREE.Scene();
scene.background = new THREE.Color(0xdfe7ee);
scene.fog = new THREE.Fog(0xdfe7ee, 30, 110); // the land runs to ~90 m; the fog takes the edge

const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 500);
camera.position.set(0, 1.4, 4.5);

// lighting: soft sky + one warm sun with shadows
const hemi = new THREE.HemisphereLight(0xffffff, 0x7d8f5e, 0.8);
scene.add(hemi);
const sun = new THREE.DirectionalLight(0xfff1dc, 2.2);
sun.position.set(4, 8, 3);
sun.castShadow = true;
sun.shadow.mapSize.set(1024, 1024);
sun.shadow.camera.left = sun.shadow.camera.bottom = -8;
sun.shadow.camera.right = sun.shadow.camera.top = 8;
sun.shadow.bias = -0.0005;
scene.add(sun.target); // the shadow box follows the action each frame (see the loop)
scene.add(sun);

const groundMat = new THREE.MeshStandardMaterial({ color: 0x86a062, roughness: 1 });
const ground = new THREE.Mesh(new THREE.CircleGeometry(380, 96), groundMat); // to the sky dome's horizon; the fog takes it long before that
ground.rotation.x = -Math.PI / 2;
ground.receiveShadow = true;
scene.add(ground);

const profile = loadProfile();
const weather = new Weather(scene, sun, hemi, groundMat);
// the sky is a photo: a clear day and an overcast one, faded by the weather, tinted by the hour
const skyDome = new SkyDome(scene, 'sky/day.jpg', 'sky/overcast.jpg');
weather.dome = skyDome;
const dog = new Dog(profile, new PrimitiveBody(scene, profile));
const IDLE_HINT = `tap the ground, or talk to ${profile.name}`;
const bowl = new FoodBowl(scene, new THREE.Vector3(-1.9, 0, 2.7));
const feeding = new Feeding(dog, bowl);
const fetch = new Fetch(scene, dog);
const yard = new Yard(scene);
// something from where you are, past the fence (Whitman near Walla Walla; the nearest notable OSM building elsewhere)
const landmarks = new Landmarks(scene, yard.fence);
weather.onLocation = (lat, lon) => void landmarks.setLocation(lat, lon);
const student = new Student(scene, dog, landmarks);
// the agility course on the back lawn (behind the cornhole tee, along the far edge)
const agility = new Agility(scene, dog, new THREE.Vector3(0.5, 0, 9.5));
const care = new CareActions(scene, dog, new THREE.Vector3(-2.2, 0, 3.4));
const home = new Home(scene, dog, new THREE.Vector3(-3.4, 0, 2.2), 0.55);
const business = new Business(scene, dog, [bowl.root.position, care.waterBowl.position, yard.lamp.position, home.root.position]);
// solid things the toy can hit
const colliders = new Colliders();
for (const t of yard.trunks) colliders.cylinder('tree', t.x, t.z, t.r, 2.2);
colliders.cylinder('lamp', yard.lamp.position.x, yard.lamp.position.z, 0.06, 2.6);
colliders.box('kennel', home.root.position.clone().add(new THREE.Vector3(0, 0.5 * dog.profile.size, 0)), home.root.rotation.y, new THREE.Vector3(0.55 * dog.profile.size, 0.5 * dog.profile.size, 0.6 * dog.profile.size));
// the fence in two runs, leaving the gate open
colliders.wall('fence', yard.fenceA.clone().setY(0), yard.gateA.clone(), yard.fenceH, 0.08);
colliders.wall('fence', yard.gateB.clone(), yard.fenceB.clone().setY(0), yard.fenceH, 0.08);
fetch.colliders = colliders;
// she stays out of trunks and walls too (her own kennel excepted): body and nose, at chest height
const _dogProbe = new THREE.Vector3();
const _dogNose = new THREE.Vector3();
const _still = new THREE.Vector3();
dog.keepOut = (p, heading, out) => {
  const size = dog.profile.size;
  const x0 = p.x;
  const z0 = p.z;
  _dogProbe.set(p.x, 0.4 * size, p.z);
  _still.set(0, 0, 0);
  colliders.resolve(_dogProbe, _still, 0.22 * size, 0, 'kennel');
  p.x = _dogProbe.x;
  p.z = _dogProbe.z;
  _dogNose.set(p.x + Math.sin(heading) * 0.55 * size, 0.4 * size, p.z + Math.cos(heading) * 0.55 * size);
  const nx = _dogNose.x;
  const nz = _dogNose.z;
  _still.set(0, 0, 0);
  colliders.resolve(_dogNose, _still, 0.1 * size, 0, 'kennel');
  p.x += _dogNose.x - nx;
  p.z += _dogNose.z - nz;
  return out.set(p.x - x0, 0, p.z - z0);
};
// beyond the fence: a lane, neighbours, trees and bushes out to the fog
const neighbourhood = new Neighbourhood(scene, yard, colliders);
// people: photo cutouts (src/world/People.ts). Ed walks a loop: out along the lane, then across the western grass and back
const ed = new Person(
  'Ed',
  'people/ed.png',
  1.8,
  [
    new THREE.Vector3(-3, 0, -9.2),
    new THREE.Vector3(-9, 0, -10),
    new THREE.Vector3(-16, 0, -13),
    new THREE.Vector3(-21, 0, -6),
    new THREE.Vector3(-27, 0, 2),
    new THREE.Vector3(-21, 0, 9),
    new THREE.Vector3(-15, 0, 3),
    new THREE.Vector3(-14, 0, -7),
  ],
  colliders,
);
scene.add(ed.root);
// Erika walks the east field
const erika = new Person('Erika', 'people/erika.png', 1.68, [new THREE.Vector3(14, 0, 4), new THREE.Vector3(22, 0, 10), new THREE.Vector3(18, 0, 20), new THREE.Vector3(8, 0, 18), new THREE.Vector3(10, 0, 8)], colliders);
scene.add(erika.root);
// Zach comes out now and then: in at the gate, two laps of the tree, one of the doghouse, out again
const zach = new Person('Zach', 'people/zach-full.png', 1.85, [yard.gateOutside.clone()], colliders);
zach.hide();
scene.add(zach.root);
const people = [ed, erika, zach];
const zachRoute = (): THREE.Vector3[] => {
  const pts: THREE.Vector3[] = [yard.gateOutside.clone(), yard.gateInside.clone()];
  const ring = (c: THREE.Vector3, r: number, laps: number, from: THREE.Vector3): void => {
    const start = Math.atan2(from.z - c.z, from.x - c.x);
    const n = 10;
    for (let i = 0; i <= n * laps; i++) {
      const a = start + (i / n) * Math.PI * 2;
      pts.push(new THREE.Vector3(c.x + Math.cos(a) * r, 0, c.z + Math.sin(a) * r));
    }
  };
  const tree = new THREE.Vector3(yard.trunks[0].x, 0, yard.trunks[0].z);
  ring(tree, 1.7, 2, yard.gateInside);
  ring(home.root.position, 1.6, 1, tree);
  pts.push(yard.gateInside.clone(), yard.gateOutside.clone());
  return pts;
};
let zachNext = performance.now() + (90 + Math.random() * 120) * 1000;
const zachRun = (): void => {
  if (zach.active) return;
  zachNext = Infinity;
  zach.onDone = () => {
    zachNext = performance.now() + (150 + Math.random() * 240) * 1000;
  };
  zach.run(zachRoute(), 3.0);
  dog.notice(yard.gateInside);
  say("Zach's here! 🏃");
};
// yard games: cornhole board facing the default camera, cans on the fence posts
// the board faces the open grass (+z), so the tee and the camera behind it have room
const games = new Games(scene, fetch, dog, new THREE.Vector3(3.2, 0, 2.0), 0, yard.postTops);
let sessionScore = 0;
games.onScore = (e) => {
  sessionScore += e.points;
  say(`${e.text} · score ${sessionScore}`);
  household.care.record(e.kind === 'can' ? 'can' : e.kind === 'hole' ? 'hole' : 'play', { hunger: dog.hunger, bowlFill: bowl.fill });
  if (sessionScore > (household.care.state.gameBest ?? 0)) {
    household.care.state.gameBest = sessionScore;
    household.markDirty();
  }
};
fetch.onHit = (name, speed) => {
  dog.sounds.play('thud', Math.min(0.9, 0.3 + speed * 0.08));
  if (name === 'tree' && !visitors.active && Math.random() < 0.35) {
    visitors.trigger('squirrel');
    say('you shook a squirrel loose!');
  }
  // a visitor in the way gets hit too
  const vp = visitors.position;
  if (vp && vp.distanceTo(fetch.root.position) < 0.55 && visitors.hitByToy()) household.care.record('play', { hunger: dog.hunger, bowlFill: bowl.fill });
};
yard.addPath(scene, [home.door.clone(), bowl.root.position.clone().add(new THREE.Vector3(0.5, 0, 0.5)), new THREE.Vector3(1.2, 0, -0.6), new THREE.Vector3(3.2, 0, -4.6), yard.gateInside.clone()]);
yard.addPath(scene, [new THREE.Vector3(1.2, 0, -0.6), yard.gateInside.clone()], 0.5);
home.onMessage = (t) => {
  if (t) say(t);
};
const visitors = new Visitors(scene, yard, dog);
visitors.onMessage = (t) => say(t);
visitors.onInterrupt = () => fetch.cancel();
visitors.onChased = () => household.care.record('visitor', { hunger: dog.hunger, bowlFill: bowl.fill });


// audio + iOS motion sensors need one user gesture to unlock; every tap/click/key counts
const motion = new DeviceMotionInput();
motion.onShake = (strength) => dog.startle(strength, camera.position);
let weatherStarted = false;
const unlock = (): void => {
  dog.sounds.unlock();
  void motion.requestPermission();
  if (!weatherStarted) {
    weatherStarted = true;
    weather.start();
  }
};
for (const ev of ['pointerdown', 'keydown', 'touchend'] as const) window.addEventListener(ev, unlock, { passive: true });

// dog switcher: one dog at a time; swapping reloads with the other profile
const other = profile.id === 'luna' ? PROFILES.tango : PROFILES.luna;
const switchBtn = document.querySelector<HTMLButtonElement>('#dogSwitch')!;
switchBtn.textContent = `${profile.name} ⇄ ${other.name}`;
const switchDog = (): void => {
  saveProfile(other.id);
  location.reload();
};
switchBtn.addEventListener('click', switchDog);
// once she's adopted, the dog is the dog: the switcher goes. (`?dog=tango` still swaps, for testing;
// adopting a second dog is a separate feature, not a toggle.)
{
  const want = new URLSearchParams(location.search).get('dog');
  if ((want === 'luna' || want === 'tango') && want !== profile.id) switchDog();
}

// the household: care record, care code, sync
const household = new Household(profile.id);
const license = new License(household);
const carePanel = new CarePanel(household, profile.name);
carePanel.license = license;
carePanel.onDorm = (name) => landmarks.setDorm(name);
// a new name / avatar shows on the holder chip at once if it's you
carePanel.onMeChange = () => {
  if (license.mine && license.holder && license.me) {
    license.holder.name = license.me.name;
    license.holder.avatar = license.me.avatar;
    license.onHolderChange?.(license.holder, true);
  }
};
const push = new Push(household, license);
{
  const btn = document.querySelector<HTMLButtonElement>('#pushToggle')!;
  const msg = document.querySelector<HTMLElement>('#pushMsg')!;
  const refresh = (): void => {
    btn.textContent = push.enabled ? 'turn off notifications' : 'turn on notifications';
    if (!push.supported) msg.textContent = /iPhone|iPad/.test(navigator.userAgent) ? 'on iPhone: add Lunaloo to your home screen first, then come back here' : 'not available in this browser';
  };
  refresh();
  btn.addEventListener('click', () => {
    if (push.enabled) {
      void push.disable().then(refresh);
      return;
    }
    void push.enable().then((r) => {
      msg.textContent = r === 'on' ? 'on — you should get a test buzz' : r;
      refresh();
    });
  });
}
const tricksRaw = localStorage.getItem(`lunaloo.tricks.${profile.id}`);
const tricks = new Tricks(dog, tricksRaw ? { ...emptyTricks(), ...(JSON.parse(tricksRaw) as Partial<TrickLevels>) } : undefined);
tricks.onMessage = (t) => say(t, true);
tricks.onChange = () => {
  localStorage.setItem(`lunaloo.tricks.${profile.id}`, JSON.stringify(tricks.levels));
  household.markDirty();
};
tricks.onLearned = () => household.care.record('trick', { hunger: dog.hunger, bowlFill: bowl.fill });
household.bindTricks(
  () => tricks.levels,
  (t) => tricks.merge(t as Partial<TrickLevels>),
);
business.onMessage = (t) => say(t, t.includes('…'));
business.onCare = (ev) => household.care.record(ev, { hunger: dog.hunger, bowlFill: bowl.fill });
business.onChange = () => household.markDirty();
household.bindBusiness(
  () => business.snapshot() as unknown as Record<string, unknown>,
  (b) => business.applyRemote(b as unknown as BusinessState & { at: number }),
);
care.onMessage = (t) => say(t, t.includes('…'));
care.onCare = (ev) => household.care.record(ev, { hunger: dog.hunger, bowlFill: bowl.fill });
care.onChange = () => household.markDirty();
// credits: the tills, and a toast for every change
household.care.onCredit = (delta, why) => say(`${delta > 0 ? '+' : ''}${delta} 🪙 ${why}`);
feeding.onBuy = (what) => {
  if (household.care.spend(PRICES[what], what === 'kibble' ? 'a bag of kibble' : what === 'torch' ? 'a torch' : 'treats')) return true;
  say(`not enough credits (${PRICES[what]} 🪙) — feeding, fetch, tricks and cornhole earn some`);
  return false;
};
care.onPay = (kind) => {
  // the vet is never refused: she needs it; it can leave you in debt
  if (household.care.spend(PRICES[kind], kind === 'vet' ? 'the vet' : kind === 'bath' ? 'a bath' : 'a brush', kind === 'vet')) return true;
  say(`not enough credits (${PRICES[kind]} 🪙) — feeding, fetch, tricks and cornhole earn some`);
  return false;
};
household.bindNeeds(
  () => care.snapshot() as unknown as Record<string, unknown>,
  (n) => care.applyRemote(n as unknown as NeedsState),
);
// grooming (brush / bath / vet) lives in the ⚙️ care panel; the status line says what she needs
const careEl = document.querySelector<HTMLElement>('#care')!;
const howIsShe = (): string => {
  const words = care.statusWords();
  return words.length ? `${profile.name} ${words.map((w) => (w.startsWith('needs') ? w : `is ${w}`)).join(', ')}` : `${profile.name} is fine`;
};
const daylightBtn = careEl.querySelector<HTMLButtonElement>('#careDaylight')!;
const showDaylightBtn = (): void => {
  daylightBtn.textContent = daylightOverride.value === null ? '☀️ always noon' : '🌙 back to real time';
};
daylightBtn.addEventListener('click', () => {
  const noon = daylightOverride.value === null;
  daylightOverride.value = noon ? 1 : null;
  try {
    localStorage.setItem('lunaloo.daylight', noon ? 'noon' : 'real');
  } catch {
    /* no storage */
  }
  showDaylightBtn();
  say(noon ? 'noon, all day' : 'real time again');
});
carePanel.onOpen = () => {
  careEl.querySelector<HTMLElement>('#careGroomStatus')!.textContent = howIsShe();
  showDaylightBtn();
};
careEl.querySelectorAll<HTMLButtonElement>('button[data-care]').forEach((b) =>
  b.addEventListener('click', () => {
    carePanel.close();
    if (care.start(b.dataset.care as CareAction)) notePlay();
  }),
);
household.bindFood(
  () => feeding.snapshot(),
  (f) => feeding.applyRemote(f),
);
feeding.onCare = (ev) => household.care.record(ev, { hunger: dog.hunger, bowlFill: bowl.fill });
let lastPlay = 0;
const notePlay = (): void => {
  if (performance.now() - lastPlay > 60000) {
    lastPlay = performance.now();
    household.care.record('play', { hunger: dog.hunger, bowlFill: bowl.fill });
  }
};
household.care.record('open', { hunger: dog.hunger, bowlFill: bowl.fill });
// no license is minted until someone adopts or plays as a guest; an existing one syncs now
if (household.code) void household.pull();

// days together: shown as "681d" in the gauges; missed days included
const togetherDays = (): number => daysTogether(household.care.state.togetherSince ?? household.care.state.days[0] ?? null);
let lastDays = togetherDays();

// holidays and the anniversary: hat, fence lights, confetti, a line on open
let daysGlyph = '🐾';
let holidayId: string | null = null;
const applyHoliday = (): void => {
  const h = holidayFor(new Date(), household.care.state.togetherSince ?? household.care.state.days[0] ?? null);
  const id = h?.id ?? null;
  if (id === holidayId) return;
  holidayId = id;
  dog.setHat(h?.hat ?? null);
  yard.setLights(h?.lights ?? false);
  weather.confetti = h?.confetti ?? false;
  daysGlyph = h ? h.emoji : '🐾';
  if (h) {
    // Thanksgiving: the extra treats the line promises, once per day for the whole license
    const extra = h.id === 'thanksgiving' && feeding.holidayTreats(today(new Date()), 3);
    say(`${h.emoji} ${h.name}${extra ? ' — 3 went in the jar' : ''}`, true);
  }
};
// (first applyHoliday() call is below, once `say` and the HUD exist — on a holiday it would crash here)

// collar: the best one her care streak has earned
let collarTier = -1;
const dressUp = (): void => {
  const c = unlockedCollar(household.care.state.bestStreak);
  if (c.tier === collarTier) return;
  collarTier = c.tier;
  dog.setCollar({ color: c.color(profile), studs: c.studs, tag: c.tag, tier: c.tier });
};
dressUp();

// the album: real photos and clips of this dog, plus photos taken here
const album = new Album(profile.id, profile.name);
album.localPhotos = () => PhotoMode.load(profile.id);
album.onDeleteLocal = (at) => PhotoMode.remove(profile.id, at);
// photos taken here go to everyone on the license; the taker or the owner can take one down
album.remotePhotos = () => household.photos();
album.canDeleteRemote = (device) => device === license.device || license.me?.role === 'owner';
album.onDeleteRemote = async (at) => {
  const ok = await household.deletePhoto(license.device, at);
  if (ok) PhotoMode.remove(profile.id, at);
  return ok;
};
const photo = new PhotoMode(renderer, scene, camera, profile, household.care, () => unlockedCollar(household.care.state.bestStreak).name);
const flash = document.querySelector<HTMLElement>('#flash')!;
// photo: step round to a three-quarter front view at her eye level, let her look at you, snap, step back
let photoBusy = false;
const savedCam = new THREE.Vector3();
document.querySelector<HTMLButtonElement>('#photoBtn')!.addEventListener('click', () => {
  if (photoBusy) return;
  photoBusy = true;
  savedCam.copy(camera.position);
  const side = Math.random() < 0.5 ? 1 : -1;
  const dir = new THREE.Vector3(0.55 * side, 0, 1).normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), dog.root.rotation.y);
  const dist = 1.9 * dog.profile.size;
  camera.position.copy(dog.root.position).addScaledVector(dir, dist).setY(0.75 * dog.profile.size);
  camLook.copy(dog.root.position);
  dog.lookAt(camera.position);
  say('say cheese…', true);
  window.setTimeout(() => {
    const snap = photo.take();
    flash.hidden = false;
    window.setTimeout(() => (flash.hidden = true), 400);
    camera.position.copy(savedCam);
    photoBusy = false;
    album.invalidate();
    void household.sharePhoto(license.device, snap).then((ok) => {
      if (ok) album.invalidate();
    });
    say('saved to the album (🎒) — tap here to share', true);
    hud.onclick = () => {
      hud.onclick = null;
      void photo.share(snap);
    };
    window.setTimeout(() => (hud.onclick = null), 8000);
    household.care.record('play', { hunger: dog.hunger, bowlFill: bowl.fill });
  }, 900);
});
const inventory = new Inventory({
  bags: () => feeding.bags,
  treats: () => feeding.treats,
  toy: () => lastToy,
  setToy: (k) => setToy(k),
  care: household.care,
  tricks: () => tricks.levels,
  dogName: profile.name,
  openAlbum: () => void album.open(),
  openShop: () => feeding.openShop(),
  openCare: () => void carePanel.open(),
});
document.querySelector<HTMLButtonElement>('#invBtn')!.addEventListener('click', () => inventory.open());
document.querySelector<HTMLButtonElement>('#gearBtn')!.addEventListener('click', () => void carePanel.open('care'));

// long-press must not open the context menu / text callout on phones
canvas.addEventListener('contextmenu', (e) => e.preventDefault());
const picker = new GroundPicker(canvas, camera);
// desktop: keys and a first-person walker (wired up below, once the camera rig exists)
const keys = new Keyboard();
const walker = new Walker();
picker.onTap = (p) => {
  if (teleportArmed) {
    teleportArmed = false;
    teleportBtn.classList.remove('armed');
    cameraTouched();
    teleportTo = p.clone();
    if (teleportTo.length() > 80) teleportTo.setLength(80);
    say(IDLE_HINT, true);
    return;
  }
  fetch.cancel();
  dog.goTo(p);
  notePlay();
};
dog.root.userData.tapName = 'dog';
picker.pickables = [...people.map((p) => p.root), bowl.root, fetch.root, care.waterBowl, dog.root, home.root, ...business.pickables, ...games.pickables, agility.root, landmarks.root];
// petting: a stroke or a held finger on her. A small hand follows the finger over her coat.
picker.petTarget = dog.root;
const petHand = new THREE.Group();
{
  const skin = new THREE.MeshStandardMaterial({ color: 0xe8b894, roughness: 0.9 });
  const palm = new THREE.Mesh(new THREE.SphereGeometry(0.055, 12, 8), skin);
  palm.scale.set(1, 0.45, 1.2);
  petHand.add(palm);
  for (let i = 0; i < 4; i++) {
    const f = new THREE.Mesh(new THREE.CapsuleGeometry(0.013, 0.06, 3, 6), skin);
    f.position.set(-0.036 + i * 0.024, 0, 0.075);
    f.rotation.x = Math.PI / 2 - 0.25;
    petHand.add(f);
  }
  petHand.visible = false;
  scene.add(petHand);
}
let petStroke = 0;
const placeHand = (p: THREE.Vector3): void => {
  petHand.position.copy(p).y += 0.02;
  petHand.rotation.y = dog.root.rotation.y + Math.PI;
  petHand.rotation.z = Math.sin(petStroke * 6) * 0.15;
};
picker.onPetStart = (p) => {
  fetch.cancel();
  dog.petStart(p);
  petStroke = 0;
  placeHand(p);
  petHand.visible = true;
  say(Math.random() < 0.5 ? `${profile.name} leans in…` : 'good spot');
};
picker.onPetMove = (p) => {
  dog.petMove(p);
  if (p) {
    petStroke += 0.2;
    placeHand(p);
  }
};
picker.onPetEnd = (seconds) => {
  dog.petEnd();
  petHand.visible = false;
  if (seconds > 1) notePlay();
};

// The placeholder shows at once; the rigged model takes over when it has loaded.
// `?body=primitive` keeps the capsules (for A/B on the phone); `lunaloo.setBody('rigged'|'primitive')` swaps live.
async function setBody(kind: 'rigged' | 'primitive'): Promise<void> {
  const body = kind === 'rigged' ? await RiggedBody.load(scene, profile) : new PrimitiveBody(scene, profile);
  const old = dog.root;
  dog.setBody(body);
  picker.pickables = picker.pickables.map((o) => (o === old ? dog.root : o));
  picker.petTarget = dog.root;
}
if (new URLSearchParams(location.search).get('body') !== 'primitive') {
  setBody('rigged').catch((e) => console.warn('rigged dog failed to load, keeping the placeholder', e));
}
picker.onTapObject = (name, obj) => {
  if (name === 'pile') {
    business.tapPile(obj);
    notePlay();
    return;
  }
  if (name === 'bowl') feeding.tapBowl();
  else if (name === 'water') care.tapWater();
  else if (name === 'house') {
    if (home.inside) {
      dog.leaveHome(home.door);
      say(`${profile.name} comes out`);
    } else say(`${profile.name}'s house — she'll go in at night or when it rains`);
  }
  else if (name === 'dog') {
    dog.notice(camera.position);
    // five quick taps: a belly rub
    tapsOnHer = tapsOnHer.filter((t) => performance.now() - t < 3000);
    tapsOnHer.push(performance.now());
    if (tapsOnHer.length >= 5) {
      tapsOnHer = [];
      dog.perform('rollover', camera.position);
      dog.praise(camera.position);
      say('belly rub! 🐾💜');
    } else say(`${howIsShe()} — brush, bath and vet are under ⚙️`);
  }
  else if (name === 'landmark') {
    if (landmarks.name === 'Whitman College') {
      const h = new Date().getHours() % 12 || 12;
      dog.sounds.chime(h);
      dog.notice(landmarks.root.position);
      say(`the Memorial Building clock strikes ${h} 🔔`);
    } else say(`${landmarks.name ?? 'a building'} — just past the fence`);
  }
  else if (name.startsWith('person:')) {
    dog.notice(obj.getWorldPosition(new THREE.Vector3()));
    say(`${name.slice(7)} waves 👋`);
  }
  else if (name === 'toy') {
    fetch.takeBack();
    say(`${fetch.kind} in hand — hold and release to throw`);
  }
  else if (name === 'cornhole') goPlay('cornhole');
  else if (name === 'can') goPlay('cans');
  else if (name === 'agility') goPlay('agility');
};
// hold to charge a throw, release to throw toward the release point
let charging = false;
picker.onHoldStart = () => {
  if (!toyOn && !treatMode) return;
  charging = true;
  say('charging…', true);
};
picker.onHoldMove = (_p, s) => {
  if (!toyOn && !treatMode) return;
  const n = Math.min(8, Math.round(((s - 0.2) / 1.3) * 8));
  say('throw ' + '▮'.repeat(Math.max(1, n)) + '▯'.repeat(8 - Math.max(1, n)), true);
};
picker.onHoldEnd = (p, s) => {
  charging = false;
  if (!toyOn && !treatMode) {
    // no toy in hand: a long press is just a tap
    fetch.cancel();
    dog.goTo(p);
    notePlay();
    return;
  }
  fetch.throw(camera.position, p, s);
  showToy();
  notePlay();
};
fetch.onMessage = (t) => say(t);
for (const p of people) {
  p.onHit = (name, kind, speed) => {
    dog.sounds.play('thud', Math.min(0.9, 0.4 + speed * 0.06));
    say(`you got ${name} with the ${kind}! 🎯`);
    dog.notice(p.root.position);
    household.care.record('play', { hunger: dog.hunger, bowlFill: bowl.fill });
  };
}
fetch.onFetched = () => {
  household.care.record('fetch', { hunger: dog.hunger, bowlFill: bowl.fill });
  // the distance challenge: a fetch from further than ever before
  const m = fetch.lastDistance;
  if (m > 2 && household.care.noteFetch(m)) {
    household.care.record('longThrow', { hunger: dog.hunger, bowlFill: bowl.fill });
    say(`new longest fetch: ${m.toFixed(1)} m 🚀`, true);
  }
};
agility.onMessage = (t) => say(t, true);
agility.onFinish = (seconds, points) => {
  household.care.record('agility', { hunger: dog.hunger, bowlFill: bowl.fill });
  const best = household.care.noteAgility(points);
  if (best) household.care.record('agilityBest', { hunger: dog.hunger, bowlFill: bowl.fill });
  say(`${seconds.toFixed(1)} s — ${points} points${best ? ' · personal best!' : ''}`, true);
  dog.excitement = 1;
  dog.lookAt(camera.position);
};
fetch.onCaught = (kind) => {
  if (kind === 'frisbee') household.care.record('catch', { hunger: dog.hunger, bowlFill: bowl.fill });
};
// the trick routine: three different tricks inside 25 s
const routine: { trick: string; at: number }[] = [];
tricks.onSuccess = (trick) => {
  const now = performance.now();
  while (routine.length && now - routine[0].at > 25_000) routine.shift();
  if (!routine.some((r) => r.trick === trick)) routine.push({ trick, at: now });
  if (routine.length >= 3) {
    routine.length = 0;
    household.care.record('routine', { hunger: dog.hunger, bowlFill: bowl.fill });
    dog.excitement = 1;
    say('a routine! three tricks in a row 🎪', true);
  } else if (routine.length === 2) say('one more different trick for a routine…');
};
// the squirrel race: catch up before it reaches the tree
visitors.onRace = (won) => {
  if (won) household.care.record('squirrelWin', { hunger: dog.hunger, bowlFill: bowl.fill });
  else say('the squirrel made the tree first');
};
// what's in your hand: nothing, ball, stick, or a treat. The toy button cycles off → ball → stick → off;
// the treat button toggles treat mode, which takes over from the toy (and vice versa).
const toyBtn = document.querySelector<HTMLButtonElement>('#toyBtn')!;
const treatBtn = document.querySelector<HTMLButtonElement>('#treatBtn')!;
let toyOn = false;
let lastToy: 'ball' | 'stick' | 'frisbee' | 'labubu' = 'ball';
let treatMode = false;
const hand = new THREE.Group();
{
  // a hand holding a treat, parked at the bottom of the view
  const skinMat = new THREE.MeshStandardMaterial({ color: 0xe0b48a, roughness: 0.9 });
  const palm = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.025, 0.11), skinMat);
  for (const [x, l] of [
    [-0.03, 0.06],
    [-0.01, 0.07],
    [0.01, 0.07],
    [0.03, 0.06],
  ] as const) {
    const f = new THREE.Mesh(new THREE.CapsuleGeometry(0.009, l, 3, 6), skinMat);
    f.rotation.x = Math.PI / 2;
    f.position.set(x, 0, -0.055 - l / 2);
    hand.add(f);
  }
  const bone = new THREE.Mesh(new THREE.CapsuleGeometry(0.014, 0.05, 4, 8), new THREE.MeshStandardMaterial({ color: 0xe8d3a8, roughness: 0.8 }));
  bone.rotation.z = Math.PI / 2;
  bone.position.set(0, 0.024, -0.03);
  hand.add(palm, bone);
  hand.position.set(0.16, -0.22, -0.55);
  hand.rotation.x = 0.35;
  hand.visible = false;
  camera.add(hand);
  scene.add(camera);
}
// what you're holding, out in front of you like a first-person game: a hand with the ball, stick or
// frisbee in it, or — for the Labubu — Annabelle's own hand holding it, from the photo
const held = new THREE.Group();
const heldToys = new Map<string, THREE.Object3D>();
const heldCard = new THREE.Mesh(
  new THREE.PlaneGeometry(0.52 * (482 / 768), 0.52),
  new THREE.MeshStandardMaterial({ alphaTest: 0.5, side: THREE.DoubleSide, roughness: 1, transparent: true }),
);
{
  const skinMat = new THREE.MeshStandardMaterial({ color: 0xe0b48a, roughness: 0.9 });
  const hand2 = new THREE.Group();
  hand2.add(new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.025, 0.11), skinMat));
  for (const [x, l] of [
    [-0.03, 0.06],
    [-0.01, 0.07],
    [0.01, 0.07],
    [0.03, 0.06],
  ] as const) {
    const f = new THREE.Mesh(new THREE.CapsuleGeometry(0.009, l, 3, 6), skinMat);
    f.rotation.x = 0.55; // curled up in front of the toy, so they show
    f.position.set(x, 0.035, -0.075);
    hand2.add(f);
  }
  held.add(hand2);
  for (const k of ['ball', 'stick', 'frisbee'] as const) {
    const c = fetch.heldCopy(k);
    c.position.set(0, k === 'ball' ? 0.075 : 0.04, -0.045);
    if (k === 'frisbee') c.rotation.x = 0.25;
    c.visible = false;
    held.add(c);
    heldToys.set(k, c);
  }
  held.position.set(0.17, -0.22, -0.55);
  held.rotation.x = 0.35;
  camera.add(held);
  new THREE.TextureLoader().load('toys/labubu-hand.png', (t) => {
    t.colorSpace = THREE.SRGBColorSpace;
    (heldCard.material as THREE.MeshStandardMaterial).map = t;
    (heldCard.material as THREE.MeshStandardMaterial).needsUpdate = true;
  });
  heldCard.position.set(0.24, -0.3, -0.62);
  heldCard.rotation.z = -0.18;
  heldCard.visible = false;
  camera.add(heldCard);
}
const syncHeld = (): void => {
  const holding = toyOn && fetch.inHand && !treatMode;
  held.visible = holding && lastToy !== 'labubu';
  heldCard.visible = holding && lastToy === 'labubu';
  for (const [k, o] of heldToys) o.visible = k === lastToy;
};
const showToy = (): void => {
  toyBtn.textContent = lastToy === 'ball' ? '🎾' : lastToy === 'stick' ? '🪵' : lastToy === 'frisbee' ? '🥏' : '🧸';
  toyBtn.classList.toggle('off', !toyOn);
  treatBtn.classList.toggle('on', treatMode);
  hand.visible = treatMode && fetch.inHand;
};
const setToy = (mode: 'off' | 'ball' | 'stick' | 'frisbee' | 'labubu'): void => {
  toyOn = mode !== 'off';
  if (mode !== 'off') {
    lastToy = mode;
    treatMode = false;
    fetch.setKind(mode);
  } else fetch.takeBack();
  showToy();
  say(
    mode === 'off'
      ? 'toys away'
      : mode === 'ball'
        ? 'ball: hold and release to throw'
        : mode === 'stick'
          ? 'stick: hold and release to throw'
          : mode === 'frisbee'
            ? 'frisbee: a long, flat throw — she jumps for it'
            : 'Labubu: hold and release to throw — it tumbles',
  );
};
// where "you" are while offering a treat: fixed when the hand comes out, so she can actually arrive
const youPoint = new THREE.Vector3();
const setTreatMode = (on: boolean): void => {
  if (on && feeding.treats <= 0) {
    // nothing to offer: the shop
    feeding.openShop();
    say('need to buy treats');
    return;
  }
  treatMode = on;
  if (on) {
    toyOn = false;
    fetch.takeBack();
    fetch.setKind('treat');
    youPoint.copy(camera.position).setY(0);
    // hold the view where you're standing; following her would back the camera away as she comes
    rig.follow = false;
    rig.lastInput = performance.now();
    teleportTo = null;
    rig.target.copy(youPoint).addScaledVector(new THREE.Vector3(-Math.sin(rig.yaw), 0, -Math.cos(rig.yaw)), 2.2);
    treatCalled = 0;
    dog.notice(camera.position);
    say('treat in hand — hold and release to toss it');
  } else {
    fetch.takeBack();
    fetch.setKind(lastToy);
    rig.follow = true;
    recenterBtn.hidden = true;
    say(IDLE_HINT, true);
  }
  showToy();
};
// --- game stations: put you (and her) where the game is played, ball in hand, camera facing it.
// Cornhole is like golf: you stand at the tee and throw at the board. Tap the board / a can, or 🎳.
const playBtn = document.querySelector<HTMLButtonElement>('#playBtn')!;
let playing: 'cornhole' | 'cans' | 'agility' | null = null;
const _tee = new THREE.Vector3();
const _aim = new THREE.Vector3();
const goPlay = (kind: 'cornhole' | 'cans' | 'agility'): void => {
  playing = kind;
  playBtn.classList.add('on');
  if (treatMode) setTreatMode(false);
  if (kind === 'agility') {
    // watch from the side; she runs it on her own
    cameraTouched();
    teleportTo = null;
    rig.follow = false;
    rig.target.copy(agility.viewAt);
    rig.yaw = 0;
    rig.pitch = 0.32;
    rig.dist = 7.5;
    fetch.cancel();
    if (toyOn) setToy('off');
    if (agility.start()) say('agility — she runs the course against the clock', true);
    return;
  }
  if (kind === 'cornhole') {
    games.tee(_tee);
    _aim.copy(games.boardPosition);
  } else {
    games.cansCentre(_aim).setY(0);
    // stand a few metres inside the fence from the middle of the cans
    _tee.copy(_aim).sub(yard.gateOutside.clone().sub(yard.gateInside).setLength(3.2));
  }
  // camera behind the tee, looking at the target; she trots over to stand beside you
  cameraTouched();
  teleportTo = null;
  rig.follow = false;
  rig.target.copy(_tee).setY(0);
  const fx = _aim.x - _tee.x;
  const fz = _aim.z - _tee.z;
  rig.yaw = Math.atan2(-fx, -fz);
  rig.pitch = 0.28;
  rig.dist = 3.4;
  dog.goTo(_tee.clone().add(new THREE.Vector3(Math.cos(rig.yaw), 0, -Math.sin(rig.yaw)).multiplyScalar(0.9)));
  if (!toyOn || lastToy !== 'ball') setToy('ball');
  say(kind === 'cornhole' ? 'cornhole — hold and release to throw at the board (3 for the hole)' : 'the cans — knock them off the posts (2 each)', true);
};
const stopPlay = (): void => {
  playing = null;
  agility.cancel();
  playBtn.classList.remove('on');
  rig.follow = true;
  recenterBtn.hidden = true;
  say(IDLE_HINT, true);
};
playBtn.addEventListener('click', () => {
  if (playing === null) goPlay('cornhole');
  else if (playing === 'cornhole') goPlay('cans');
  else if (playing === 'cans') goPlay('agility');
  else stopPlay();
});
// the toy button cycles off → ball → stick → frisbee → off
toyBtn.addEventListener('click', () => setToy(!toyOn ? 'ball' : lastToy === 'ball' ? 'stick' : lastToy === 'stick' ? 'frisbee' : lastToy === 'frisbee' ? 'labubu' : 'off'));
treatBtn.addEventListener('click', () => {
  setTreatMode(!treatMode);
  notePlay();
});
fetch.onTreatEaten = () => {
  if (!feeding.giveTreat()) return;
  say(tricks.reward() ? 'treat — she got the lesson' : 'treat — yum');
  if (feeding.treats <= 0) setTreatMode(false);
  showToy();
};
// she comes for a treat in your hand
let treatCalled = 0;
let lastHandTreat = -1e9;
const treatFromHand = (): void => {
  if (!treatMode || !fetch.inHand || document.body.classList.contains('watching')) return;
  const chewing = performance.now() - lastHandTreat < 6000;
  hand.visible = !chewing; // the hand goes back in the pocket while she chews
  if (chewing) return;
  const d = dog.root.position.distanceTo(youPoint);
  if (d < 1.6 && !dog.moving) {
    lastHandTreat = performance.now();
    dog.enjoyTreat();
    dog.lookAt(camera.position); // chewing, but looking at you
    fetch.onTreatEaten?.();
    return;
  }
  // a treat outranks wandering, sniffing and bowl trips — not a visitor, a meal in progress, or a trick
  if (!dog.busy && !dog.eating && !dog.trickActive && performance.now() - treatCalled > 3000) {
    treatCalled = performance.now();
    const p = dog.root.position.clone().sub(youPoint).setY(0);
    const dd = p.length();
    if (dd > 1.4) {
      p.multiplyScalar(1.2 / dd).add(youPoint).setY(0);
      dog.goTo(p, true);
      dog.lookAt(camera.position);
    }
  }
};

// --- talking to the dog: mic (Web Speech API) or the text box
const hud = document.querySelector<HTMLElement>('#hud')!;
hud.textContent = IDLE_HINT;
const micBtn = document.querySelector<HTMLButtonElement>('#mic')!;
const sayInput = document.querySelector<HTMLInputElement>('#say')!;
let hudTimer = 0;
function say(text: string, sticky = false): void {
  hud.textContent = text;
  window.clearTimeout(hudTimer);
  if (!sticky) hudTimer = window.setTimeout(() => (hud.textContent = voice.listening ? 'listening…' : IDLE_HINT), 2500);
}
applyHoliday();
const gauges = new Gauges();
feeding.onMessage = (t) => say(t);
// a landmark appears: the line is tappable and swings the view over to it
const lookAtLandmark = (): void => {
  cameraTouched();
  teleportTo = null;
  rig.target.copy(landmarks.root.position).setY(1.4);
  rig.yaw = yard.fence.rotation.y + Math.PI;
  rig.pitch = 0.12;
  rig.dist = 10;
  say(`${landmarks.name} — 🎯 goes back to ${profile.name}`);
};
landmarks.onMessage = (t) => {
  say(`${t} — tap here to look`, true);
  hud.onclick = () => {
    hud.onclick = null;
    lookAtLandmark();
  };
  window.setTimeout(() => (hud.onclick = null), 15000);
};
(window as unknown as { lookAtLandmark: () => void }).lookAtLandmark = lookAtLandmark;

// --- cover-screen mode: on the Razr's outer display (a small, near-square viewport) or `?cover=1`.
// The yard fills the screen with a clock over it; after a quiet spell it dims and she goes to bed.
const coverClock = document.querySelector<HTMLElement>('#coverClock')!;
const coverDim = document.querySelector<HTMLElement>('#coverDim')!;
let coverMode = false;
let coverIdle = 0;
let coverAsleep = false;
const isCoverShape = (): boolean => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return w <= 520 && h / w > 0.7 && h / w < 1.45;
};
const setCoverMode = (on: boolean): void => {
  if (on === coverMode) return;
  coverMode = on;
  document.body.classList.toggle('cover', on);
  coverClock.hidden = !on;
  coverDim.hidden = !on;
  closeTopPanel();
  if (on) {
    coverIdle = 0;
    rig.follow = true;
    rig.dist = 2.6;
    rig.pitch = 0.22;
    if (Capacitor.isNativePlatform()) void KeepAwake.keepAwake().catch(() => undefined);
  } else {
    coverWake();
    rig.dist = 4.7;
    rig.pitch = 0.3;
    if (Capacitor.isNativePlatform()) void KeepAwake.allowSleep().catch(() => undefined);
  }
};
const coverWake = (): void => {
  coverIdle = 0;
  if (!coverAsleep) return;
  coverAsleep = false;
  coverDim.classList.remove('on');
  renderer.toneMappingExposure = 1.0;
  if (home.inside || dog.homing) dog.leaveHome(home.door);
};
const coverTick = (dt: number): void => {
  if (!coverMode) return;
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const t = `${hh}:${mm}`;
  const timeEl = document.querySelector<HTMLElement>('#coverTime')!;
  if (timeEl.textContent !== t) {
    timeEl.textContent = t;
    document.querySelector<HTMLElement>('#coverDate')!.textContent = `${now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} · ${weather.daylight < 0.5 ? '🌙' : weather.sky === 'rain' ? '🌧️' : '☀️'}${weather.temperatureC !== null ? ` ${Math.round(weather.temperatureC)}°` : ''}`;
  }
  coverIdle += dt;
  // after a quiet minute and a half: dim, and she takes herself off to bed
  if (!coverAsleep && coverIdle > 90) {
    coverAsleep = true;
    coverDim.classList.add('on');
    renderer.toneMappingExposure = 0.55;
    if (!home.inside && !dog.homing) dog.goHome(home.door, home.bed);
  }
};
window.addEventListener('pointerdown', () => coverWake(), { capture: true });
const coverParam = new URLSearchParams(location.search).get('cover');
const evalCover = (): void => setCoverMode(coverParam === '1' || (coverParam !== '0' && isCoverShape()));
window.addEventListener('resize', evalCover);
window.setTimeout(evalCover, 0); // once everything below exists

// --- the bark alarm (phone app only)
const alarm = new Alarm(profile.id, profile.name);
{
  const timeIn = document.querySelector<HTMLInputElement>('#alarmTime')!;
  const toggle = document.querySelector<HTMLButtonElement>('#alarmToggle')!;
  const amsg = document.querySelector<HTMLElement>('#alarmMsg')!;
  const show = (): void => {
    timeIn.value = `${String(alarm.setting.hour).padStart(2, '0')}:${String(alarm.setting.minute).padStart(2, '0')}`;
    toggle.textContent = alarm.setting.on ? 'turn off' : 'turn on';
    if (!alarm.available) amsg.textContent = 'the bark alarm works in the phone app';
  };
  const applyAlarm = (on: boolean): void => {
    const [h, m] = timeIn.value.split(':').map(Number);
    void alarm.apply({ hour: h || 0, minute: m || 0, on }).then((t) => {
      amsg.textContent = t;
      show();
    });
  };
  toggle.addEventListener('click', () => applyAlarm(!alarm.setting.on));
  timeIn.addEventListener('change', () => {
    if (alarm.setting.on) applyAlarm(true);
  });
  show();
}

// --- the phone's back button / browser back: close whatever's open first, then recentre, then leave
const closeTopPanel = (): boolean => {
  const shop = document.querySelector<HTMLElement>('#shop')!;
  if (!shop.hidden) return ((shop.hidden = true), true);
  if (!msgSheet.hidden) return ((msgSheet.hidden = true), true);
  if (!meMenu.hidden) return ((meMenu.hidden = true), true);
  if (album.isOpen) return (album.close(), true);
  if (carePanel.isOpen) return (carePanel.close(), true);
  if (inventory.isOpen) return (inventory.close(), true);
  if (!adoptEl.hidden) return true; // the adoption screen has to be finished, not backed out of
  if (!rig.follow) {
    rig.follow = true;
    recenterBtn.hidden = true;
    if (playing) stopPlay();
    return true;
  }
  return false;
};
if (Capacitor.isNativePlatform()) {
  void CapApp.addListener('backButton', () => {
    if (!closeTopPanel()) void CapApp.minimizeApp();
  });
} else {
  // on the web, every open panel pushes a history entry so the browser's back closes it
  history.replaceState({ lunaloo: 'yard' }, '');
  window.addEventListener('popstate', () => {
    if (closeTopPanel()) history.pushState({ lunaloo: 'panel' }, '');
  });
  for (const el of ['#inventory', '#care', '#album', '#shop']) {
    const node = document.querySelector<HTMLElement>(el)!;
    new MutationObserver(() => {
      if (!node.hidden && history.state?.lunaloo !== 'panel') history.pushState({ lunaloo: 'panel' }, '');
    }).observe(node, { attributes: true, attributeFilter: ['hidden'] });
  }
}
student.onMessage = (t) => say(t);
// `?place=whitman` forces a curated landmark wherever you are (for looking at it from home)
const forcedPlace = new URLSearchParams(location.search).get('place');
if (forcedPlace === 'nearby') localStorage.setItem('lunaloo.place', 'nearby');
else if (forcedPlace && !landmarks.force(forcedPlace)) say(`no landmark called ${forcedPlace}`);
// the campus is the backdrop by default, before any location fix arrives
if (!forcedPlace && Landmarks.preference !== 'nearby') landmarks.force(Landmarks.preference);
const shopEl = document.querySelector<HTMLElement>('#shop')!;
feeding.onShopOpen = () => {
  shopEl.hidden = false;
  notePlay();
};
shopEl.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((b) =>
  b.addEventListener('click', () => {
    feeding.buy(b.dataset.buy as 'kibble' | 'treats' | 'torch');
    if (feeding.torch) document.querySelector<HTMLButtonElement>('#buyTorch')!.textContent = '🔦 torch — yours';
  }),
);
if (feeding.torch) document.querySelector<HTMLButtonElement>('#buyTorch')!.textContent = '🔦 torch — yours';
// the torch: a beam from where you stand onto her, on at night once bought
const torch = new THREE.SpotLight(0xfff1d6, 0, 14, 0.42, 0.6, 1.2);
torch.castShadow = false;
scene.add(torch, torch.target);
shopEl.querySelector<HTMLButtonElement>('.shop-close')!.addEventListener('click', () => (shopEl.hidden = true));
weather.onStatus = (t) => say(t);
feeding.onStatus = () => {
  const flags: string[] = [];
  const cw = care.statusWords();
  if (cw.includes('needs a bath')) flags.push('bath');
  else if (cw.includes('scruffy')) flags.push('scruffy');
  if (cw.includes('limping')) flags.push('vet');
  if (cw.includes('thirsty')) flags.push('thirsty');
  if (business.statusWords().length) flags.push('scoop');
  gauges.set({ bowl: bowl.fill, water: care.state.water, hunger: dog.hunger, flags, mood: household.care.moodFace, days: togetherDays(), daysGlyph, credits: household.care.credits, sky: weather.sky, night: weather.daylight < 0.5, temp: weather.temperatureC });
};
let lastHeard = '';
let tapsOnHer: number[] = [];
/** things she reacts to that aren't commands: praise, a squirrel, the school cheer, the other dog's name */
function easterEgg(text: string): boolean {
  const t = text.toLowerCase();
  if (/\b(love you|miss you|missed you)\b/.test(t)) {
    dog.comeSit(camera.position);
    say(`${profile.name} comes and sits with you 💜`, true);
    return true;
  }
  if (/\b(good (girl|dog)|who's a good|whos a good|beautiful|pretty girl)\b/.test(t)) {
    dog.praise(camera.position);
    say('she knows 💜');
    return true;
  }
  if (/\b(howl|sing|awoo+)\b/.test(t)) {
    dog.howl();
    say(`${profile.name} sings 🎶`);
    return true;
  }
  if (/\bsquirrel\b/.test(t)) {
    if (!visitors.active) visitors.trigger('squirrel');
    say('SQUIRREL!');
    return true;
  }
  if (/\b(go blues|whitman|blues)\b/.test(t)) {
    if (landmarks.hasDorm) student.trigger();
    dog.barkNow(2);
    say('Go Blues! 💙💛');
    return true;
  }
  const otherName = profile.id === 'luna' ? 'tango' : 'luna';
  if (new RegExp(`\\b${otherName}\\b`).test(t)) {
    dog.confused(camera.position);
    say(`"${otherName}"? she tilts her head — wrong dog 😉`);
    return true;
  }
  return false;
}
function heard(text: string, final: boolean): void {
  if (final && easterEgg(text)) return;
  const cmd = parseCommand(text, profile.aliases);
  if (!cmd) {
    if (final) say(`“${text}”`);
    return;
  }
  // interim results repeat the same phrase as it firms up; act once per phrase
  const key = `${cmd}:${text}`;
  if (!final && key === lastHeard) return;
  lastHeard = key;
  fetch.cancel();
  dog.hear(cmd, camera.position);
  say(`“${text}” → ${cmd}`);
}
const voice = new VoiceInput();
voice.onTranscript = heard;
voice.onStatus = (s, detail) => {
  micBtn.classList.toggle('on', s === 'listening');
  if (s === 'listening') say('listening…', true);
  else if (s === 'off') say(IDLE_HINT, true);
  else say(detail ?? s, true);
};
const micProblem = (): string | null =>
  !window.isSecureContext
    ? `mic needs https — open https://${location.host}`
    : !voice.supported
      ? 'no speech recognition in this browser — try Chrome or Safari'
      : null;
micBtn.classList.toggle('unavailable', micProblem() !== null);
micBtn.addEventListener('click', () => {
  const problem = micProblem();
  if (problem) say(problem, true);
  else voice.toggle();
});
document.querySelector<HTMLFormElement>('#talk')!.addEventListener('submit', (e) => {
  e.preventDefault();
  const text = sayInput.value.trim().toLowerCase();
  if (text) heard(text, true);
  sayInput.value = '';
});

function resize(): void {
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  // keep ~44° of horizontal view on a portrait phone (vertical fov follows), capped for desktop
  camera.fov = walker.on
    ? camera.aspect > 1
      ? 64
      : 76
    : THREE.MathUtils.clamp((2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(22)) / camera.aspect) * 180) / Math.PI, 40, 78);
  camera.updateProjectionMatrix();
}
window.addEventListener('resize', resize);
resize();

// ---- landing / adoption / sign-in. The section has three views: the landing (sign in or just play),
// the sign-in panel (email → code → pick a license), and the adoption form (new dog, or join by code).
const adoptEl = document.querySelector<HTMLElement>('#adopt')!;
const account = new Account(license.device);
{
  let chosenDog: 'luna' | 'tango' = profile.id;
  let chosenAvatar = AVATARS[Math.floor(Math.random() * AVATARS.length)];
  let joining = false;
  const q = <T extends HTMLElement>(sel: string): T => adoptEl.querySelector<T>(sel)!;
  const nameIn = q<HTMLInputElement>('#adoptName');
  const dateIn = q<HTMLInputElement>('#adoptDate');
  const codeIn = q<HTMLInputElement>('#adoptCode');
  const msg = q('#adoptMsg');
  dateIn.value = today();
  dateIn.max = today();
  const dogBtns = adoptEl.querySelectorAll<HTMLButtonElement>('.adopt-dog');
  const pickDog = (d: 'luna' | 'tango'): void => {
    chosenDog = d;
    dogBtns.forEach((b) => b.classList.toggle('selected', b.dataset.dog === d));
  };
  dogBtns.forEach((b) => b.addEventListener('click', () => pickDog(b.dataset.dog as 'luna' | 'tango')));
  pickDog(chosenDog);
  const av = q('#adoptAvatars');
  for (const a of AVATARS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = a;
    b.classList.toggle('selected', a === chosenAvatar);
    b.addEventListener('click', () => {
      chosenAvatar = a;
      av.querySelectorAll('button').forEach((x) => x.classList.toggle('selected', x === b));
    });
    av.append(b);
  }
  const setMode = (join: boolean): void => {
    joining = join;
    q('#adoptTitle').textContent = join ? 'Join a license' : 'Adopt a dog';
    q('#adoptHelp').textContent = join
      ? "Type the code from the other phone. You'll share the same dog — food, tricks, everything."
      : "She'll be yours to look after. Pick who, tell her your name, and choose the day she came home.";
    q('#adoptDateRow').hidden = join;
    q('#adoptCodeRow').hidden = !join;
    q('.adopt-dogs').hidden = join;
    q('#adoptGo').textContent = join ? 'Join' : 'Adopt';
    q('#adoptToggle').textContent = join ? 'Start fresh instead' : "I have a code from someone else's phone";
  };
  const formParts = ['.adopt-card > label', '#adoptGo', '#adoptToggle', '#adoptBack'];
  const setView = (v: 'landing' | 'signin' | 'form'): void => {
    q('#landing').hidden = v !== 'landing';
    q('#signin').hidden = v !== 'signin';
    for (const sel of formParts) adoptEl.querySelectorAll<HTMLElement>(sel).forEach((el) => (el.hidden = v !== 'form'));
    q('.adopt-dogs').hidden = v === 'signin';
    q('#adoptHelp').hidden = v !== 'form';
    msg.textContent = '';
    if (v === 'landing') q('#adoptTitle').textContent = 'Lunaloo';
    else if (v === 'signin') q('#adoptTitle').textContent = account.signedIn ? account.email! : 'Sign in';
    else setMode(joining);
  };
  /** a second adoption on this account: drop this device's license (the account keeps it) and reload into the form */
  const adoptAnother = (): void => {
    if (household.code) {
      Account.forgetLicenseLocally();
      sessionStorage.setItem('lunaloo.adoptNew', '1');
      location.reload();
      return;
    }
    joining = false;
    setView('form');
  };
  const finishIn = (line: string): void => {
    adoptEl.hidden = true;
    renderTag();
    switchBtn.hidden = true; // adopted: the dog is the dog
    lastDays = togetherDays();
    applyHoliday();
    say(line, true);
    license.start();
  };
  // the adoption form (adopt a new dog, or join by code)
  q('#adoptToggle').addEventListener('click', () => setMode(!joining));
  q('#adoptBack').addEventListener('click', () => setView(household.code ? 'signin' : 'landing'));
  q('#adoptGo').addEventListener('click', async () => {
    const name = nameIn.value.trim();
    if (!name) {
      msg.textContent = 'tell her your name first';
      return;
    }
    msg.textContent = 'one moment…';
    if (joining) {
      const ok = await household.link(codeIn.value);
      if (!ok) {
        msg.textContent = "couldn't find that code — check it and try again";
        return;
      }
    } else {
      const code = await household.ensureCode();
      if (!code) {
        msg.textContent = 'needs the internet for a moment to make your license';
        return;
      }
      household.care.state.togetherSince = dateIn.value || today();
      household.markDirty();
    }
    const joined = await license.join(name, chosenAvatar);
    if (!joined) {
      msg.textContent = "couldn't reach the server — try again";
      return;
    }
    await household.push();
    if (account.signedIn && household.code) await account.attach(household.code);
    if (!joining && chosenDog !== profile.id) {
      saveDogChoice(chosenDog);
      location.reload();
      return;
    }
    finishIn(joining ? `welcome — you're on ${profile.name}'s license now` : `${profile.name} is home 💜`);
  });
  // just play: a guest license, no name asked; signing in later keeps her
  q('#landingGuest').addEventListener('click', async () => {
    msg.textContent = 'one moment…';
    const code = await household.ensureCode();
    if (!code) {
      msg.textContent = 'needs the internet for a moment';
      return;
    }
    if (!household.care.state.togetherSince) {
      household.care.state.togetherSince = today();
      household.markDirty();
    }
    const joined = await license.join('a guest', chosenAvatar);
    if (!joined) {
      msg.textContent = "couldn't reach the server — try again";
      return;
    }
    await household.push();
    if (chosenDog !== profile.id) {
      saveDogChoice(chosenDog);
      location.reload();
      return;
    }
    finishIn(`${profile.name} is yours to play with — sign in from ⚙️ any time to keep her`);
  });
  // sign in: email → code → the account's licenses
  const emailIn = q<HTMLInputElement>('#signinEmail');
  const codeSignIn = q<HTMLInputElement>('#signinCode');
  const smsg = q('#signinMsg');
  const licList = q('#signinLicenses');
  q('#landingSignin').addEventListener('click', () => {
    setView('signin');
    emailIn.focus();
  });
  q('#signinBack').addEventListener('click', () => {
    if (household.code && license.me) adoptEl.hidden = true;
    else setView('landing');
  });
  q('#signinSend').addEventListener('click', async () => {
    smsg.textContent = 'sending…';
    const r = await account.start(emailIn.value);
    smsg.textContent = r.message;
    if (r.ok) {
      q('#signinCodeRow').hidden = false;
      q('#signinVerify').hidden = false;
      codeSignIn.focus();
    }
  });
  const renderLicenses = (): void => {
    const me = account.me;
    licList.textContent = '';
    licList.hidden = false;
    if (!me || !me.licenses.length) {
      smsg.textContent = 'no dogs on this account yet — adopt one below';
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = '🐶 adopt a dog';
      b.addEventListener('click', () => {
        joining = false;
        setView('form');
      });
      licList.append(b);
      return;
    }
    smsg.textContent = 'your dogs — tap one to bring her onto this phone';
    for (const lic of me.licenses) {
      for (const d of lic.dogs) {
        const here = household.code === lic.code && profile.id === d.dog;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = here ? 'on' : '';
        const who = lic.members.map((m) => `${m.avatar} ${m.name}`).join(', ');
        b.innerHTML = `<span></span><small></small>`;
        b.firstElementChild!.textContent = `${PROFILES[d.dog].name}${lic.name ? ` · ${lic.name}` : ''}${here ? ' · this phone' : ''}`;
        b.lastElementChild!.textContent = `${d.days} day${d.days === 1 ? '' : 's'} of care · ${who || 'nobody yet'}`;
        b.addEventListener('click', () => {
          if (here) adoptEl.hidden = true;
          else Account.switchTo(lic, d.dog);
        });
        licList.append(b);
      }
    }
    const more = document.createElement('button');
    more.type = 'button';
    more.textContent = '🐶 adopt another dog';
    more.addEventListener('click', () => adoptAnother());
    licList.append(more);
  };
  q('#signinVerify').addEventListener('click', async () => {
    smsg.textContent = 'checking…';
    const r = await account.verify(emailIn.value, codeSignIn.value, household.code);
    smsg.textContent = r.message;
    if (!r.ok) return;
    q('#signinCodeRow').hidden = true;
    q('#signinVerify').hidden = true;
    q('#adoptTitle').textContent = account.email!;
    renderLicenses();
    renderAccount();
    renderTag();
  });
  // the account section in the care panel
  const careAccountLine = document.querySelector<HTMLElement>('#careAccountLine')!;
  const careAccountBtn = document.querySelector<HTMLButtonElement>('#careAccountBtn')!;
  const careAdoptions = document.querySelector<HTMLElement>('#careAdoptions')!;
  const renderAccount = (): void => {
    careAdoptions.textContent = '';
    if (!account.signedIn) {
      careAccountLine.textContent = 'Add your email so she comes back on any phone.';
      careAccountBtn.textContent = 'add your email';
      careAccountBtn.onclick = () => {
        carePanel.close();
        adoptEl.hidden = false;
        setView('signin');
        licList.hidden = true;
        emailIn.focus();
      };
      return;
    }
    careAccountLine.textContent = `signed in as ${account.email}`;
    careAccountBtn.textContent = 'sign out';
    careAccountBtn.onclick = () => void account.signOut().then(renderAccount);
    void account.refresh().then((me) => {
      if (!me) return renderAccount();
      for (const lic of me.licenses) {
        for (const d of lic.dogs) {
          const here = household.code === lic.code && profile.id === d.dog;
          const b = document.createElement('button');
          b.type = 'button';
          b.className = here ? 'on' : '';
          b.innerHTML = `<span></span><small></small>`;
          b.firstElementChild!.textContent = `${PROFILES[d.dog].name}${lic.name ? ` · ${lic.name}` : ''}${here ? ' · this phone' : ''}`;
          b.lastElementChild!.textContent = `${d.days} day${d.days === 1 ? '' : 's'} of care · ${lic.members.map((m) => `${m.avatar} ${m.name}`).join(', ') || 'nobody yet'}`;
          if (!here) b.addEventListener('click', () => Account.switchTo(lic, d.dog));
          careAdoptions.append(b);
          if (!here) {
            // take a license off the account without touching the license itself
            const off = document.createElement('button');
            off.type = 'button';
            off.className = 'care-adoption-off';
            off.textContent = `remove from my account`;
            off.addEventListener('click', () => {
              if (!confirm(`Take ${PROFILES[d.dog].name} off your account? The license and its carers stay as they are.`)) return;
              void account.detach(lic.code).then(renderAccount);
            });
            careAdoptions.append(off);
          }
        }
      }
      const more = document.createElement('button');
      more.type = 'button';
      more.textContent = '🐶 adopt another dog';
      more.addEventListener('click', () => adoptAnother());
      careAdoptions.append(more);
    });
  };
  // the dog tag: "Ed · Luna" and the license number, so it's obvious the phone knows you
  const tagEl = document.querySelector<HTMLButtonElement>('#tag')!;
  const renderTag = (): void => {
    tagEl.hidden = !household.code || !license.me;
    if (tagEl.hidden) return;
    const me = license.me!;
    tagEl.querySelector<HTMLElement>('#tagWho')!.textContent = `${me.avatar} ${me.name} · ${profile.name}`;
    tagEl.querySelector<HTMLElement>('#tagCode')!.textContent = `${household.code}${account.signedIn ? ' ✓' : ''}`;
  };
  tagEl.addEventListener('click', () => void carePanel.open('license'));
  renderTag();
  carePanel.onMeChange = ((prev) => () => {
    prev?.();
    renderTag();
  })(carePanel.onMeChange);
  carePanel.onLeftLicense = () => {
    Account.forgetLicenseLocally();
    location.reload();
  };
  carePanel.onOpen = ((prev) => () => {
    prev?.();
    renderAccount();
  })(carePanel.onOpen);
  // this device's license, if any, belongs on the account
  if (account.signedIn && household.code) void account.attach(household.code);

  // --- the gate
  switchBtn.hidden = !!license.me;
  const joinCode = new URLSearchParams(location.search).get('join');
  if (!household.code) {
    adoptEl.hidden = false;
    if (joinCode) {
      joining = true;
      setView('form');
      codeIn.value = joinCode.trim().toUpperCase();
      nameIn.focus();
    } else if (sessionStorage.getItem('lunaloo.adoptNew')) {
      sessionStorage.removeItem('lunaloo.adoptNew');
      joining = false;
      setView('form');
    } else setView('landing');
  } else if (!license.me) {
    // a code but no introduction: maybe this device is already a member (after a switch); else ask
    adoptEl.hidden = false;
    joining = false;
    setView('form');
    q('#adoptTitle').textContent = `Who's looking after ${profile.name}?`;
    q('#adoptHelp').textContent = 'Tell her your name and pick an avatar.';
    q('#adoptDateRow').hidden = true;
    q('.adopt-dogs').hidden = true;
    q('#adoptToggle').hidden = true;
    q('#adoptBack').hidden = true;
    void license.resolveMe().then((ok) => {
      if (ok) finishIn(`${profile.name} is home 💜`);
    });
  } else {
    license.start();
  }
}

// ---- who has her: check-out banner, hand-over, messages
const msgSheet = document.querySelector<HTMLElement>('#msgSheet')!;
const setWatching = (on: boolean): void => {
  document.body.classList.toggle('watching', on);
};
// one chip, always the avatar of whoever has her; tap for the right actions
const meChip = document.querySelector<HTMLButtonElement>('#meChip')!;
const meMenu = document.querySelector<HTMLElement>('#meMenu')!;
license.onHolderChange = (holder, mine) => {
  meMenu.hidden = true;
  meChip.hidden = !holder;
  meChip.classList.toggle('theirs', !!holder && !mine);
  meChip.textContent = holder ? holder.avatar : '🐾';
  meChip.dataset.name = !holder || mine ? '' : holder.name; // your own chip needs no label
  meChip.classList.toggle('unlabelled', !holder || mine);
  meMenu.querySelector<HTMLElement>('#meEdit')!.hidden = !holder || !mine;
  meMenu.querySelector<HTMLElement>('.me-menu-text')!.textContent = !holder ? '' : mine ? `you're with ${profile.name}` : `${holder.name} is with ${profile.name}`;
  meMenu.querySelector<HTMLElement>('#meAsk')!.hidden = !holder || mine;
  meMenu.querySelector<HTMLElement>('#meHandover')!.hidden = !holder || !mine;
  if (!holder || mine) {
    setWatching(false);
    void household.pull();
  } else {
    setWatching(true);
    fetch.cancel();
    say(`${holder.avatar} ${holder.name} is with ${profile.name} — tap their avatar to ask`, true);
  }
};
meChip.addEventListener('click', () => (meMenu.hidden = !meMenu.hidden));
meMenu.querySelector<HTMLButtonElement>('#meHandover')!.addEventListener('click', () => {
  meMenu.hidden = true;
  void license.release().then(() => say(`handed ${profile.name} over`, true));
});
meMenu.querySelector<HTMLButtonElement>('#meEdit')!.addEventListener('click', () => {
  meMenu.hidden = true;
  void carePanel.open('license').then(() => document.querySelector<HTMLInputElement>('#careMeName')!.focus());
});
meMenu.querySelector<HTMLButtonElement>('#meMsg')!.addEventListener('click', () => {
  meMenu.hidden = true;
  msgSheet.hidden = !msgSheet.hidden;
});
license.onMessage = (m) => {
  say(`${m.from.avatar} ${m.from.name}: ${m.text}`, true);
  dog.notice(camera.position);
};
meMenu.querySelector<HTMLButtonElement>('#meAsk')!.addEventListener('click', () => {
  meMenu.hidden = true;
  void license.nudge('let me have her').then(() => say('asked — waiting for a hand-over', true));
});
msgSheet.querySelectorAll<HTMLButtonElement>('button[data-msg]').forEach((b) =>
  b.addEventListener('click', () => {
    msgSheet.hidden = true;
    void license.nudge(b.dataset.msg!).then(() => say('sent', false));
  }),
);
msgSheet.querySelector<HTMLFormElement>('#msgForm')!.addEventListener('submit', (e) => {
  e.preventDefault();
  const input = msgSheet.querySelector<HTMLInputElement>('input')!;
  const t = input.value.trim();
  if (!t) return;
  input.value = '';
  msgSheet.hidden = true;
  void license.nudge(t).then(() => say('sent', false));
});
for (const ev of ['pointerdown', 'keydown'] as const) window.addEventListener(ev, () => license.touch(), { passive: true });

// ---- camera rig: orbits a target that follows the dog until you move the camera yourself
const rig = { target: new THREE.Vector3(), yaw: 0, pitch: 0.3, dist: 4.7, follow: true, lastInput: -1e9 };
const recenterBtn = document.querySelector<HTMLButtonElement>('#recenter')!;
const cameraTouched = (): void => {
  rig.follow = false;
  rig.lastInput = performance.now();
  recenterBtn.hidden = false;
};
picker.onDrag = (dx, dy) => {
  if (walker.on) {
    walker.look(dx, dy, 0.004);
    return;
  }
  cameraTouched();
  rig.yaw -= dx * 0.006;
  rig.pitch = THREE.MathUtils.clamp(rig.pitch + dy * 0.004, 0.08, 1.15);
};
const _right = new THREE.Vector3();
const _fwd = new THREE.Vector3();
picker.onPinch = (scale, dx, dy) => {
  cameraTouched();
  rig.dist = THREE.MathUtils.clamp(rig.dist / scale, 1.8, 11);
  // two-finger drag pans the target across the ground
  _right.set(Math.cos(rig.yaw), 0, -Math.sin(rig.yaw));
  _fwd.set(-Math.sin(rig.yaw), 0, -Math.cos(rig.yaw));
  const k = rig.dist * 0.0022;
  rig.target.addScaledVector(_right, -dx * k).addScaledVector(_fwd, dy * k);
  if (rig.target.length() > 80) rig.target.setLength(80);
};
recenterBtn.addEventListener('click', () => {
  if (playing) stopPlay();
  stopWalk();
  rig.follow = true;
  teleportTo = null;
  recenterBtn.hidden = true;
});
// teleport: arm, then tap the ground to glide the view there (she stays where she is)
const teleportBtn = document.querySelector<HTMLButtonElement>('#teleport')!;
let teleportArmed = false;
let teleportTo: THREE.Vector3 | null = null;
teleportBtn.addEventListener('click', () => {
  teleportArmed = !teleportArmed;
  teleportBtn.classList.toggle('armed', teleportArmed);
  say(teleportArmed ? 'tap where you want to go' : IDLE_HINT, true);
});
{
  // initial rig matches the old fixed camera (0, 1.4, 4.5) looking at the origin
  rig.dist = Math.hypot(1.4, 4.5);
  rig.pitch = Math.atan2(1.4, 4.5);
}

// ---- desktop: WASD walks (first person), the mouse looks, keys do what taps do.
// The first movement key enters walk mode; 🎯 goes back to following her.
const startWalk = (): void => {
  if (walker.on) return;
  if (playing) stopPlay();
  walker.start(camera, camLook, colliders);
  rig.follow = false;
  rig.lastInput = performance.now();
  recenterBtn.hidden = false;
  teleportTo = null;
  teleportArmed = false;
  teleportBtn.classList.remove('armed');
  dog.heel = true;
  document.body.classList.add('walking');
  resize();
  say('on foot: WASD · ← → turn (or click the yard and the mouse looks) · E use · Space throw · F treat · C call · 1–5 toys · 🎯 back', true);
};
const stopWalk = (): void => {
  if (!walker.on) return;
  walker.stop();
  dog.heel = false;
  document.body.classList.remove('walking');
  if (document.pointerLockElement === canvas) document.exitPointerLock();
  resize();
};
keys.onMove = startWalk;
keys.onTalk = () => sayInput.focus();
// a click on the world takes focus off the text box (so keys work) and, on foot, locks the mouse to look
canvas.addEventListener('pointerdown', () => (document.activeElement as HTMLElement | null)?.blur?.());
canvas.addEventListener('click', () => {
  if (!walker.on || document.pointerLockElement === canvas) return;
  try {
    const r = canvas.requestPointerLock() as unknown;
    if (r instanceof Promise) r.catch(() => undefined);
  } catch {
    /* not allowed here */
  }
});
document.addEventListener('mousemove', (e) => {
  if (walker.on && document.pointerLockElement === canvas) walker.look(e.movementX, e.movementY);
});
// the wheel zooms the orbit camera
canvas.addEventListener(
  'wheel',
  (e) => {
    if (walker.on) return;
    e.preventDefault();
    cameraTouched();
    rig.dist = THREE.MathUtils.clamp(rig.dist * Math.exp(e.deltaY * 0.0015), 1.8, 11);
  },
  { passive: false },
);
// what's in the middle of the view: an object to use, or the ground to send her to / throw at
const _aimRay = new THREE.Raycaster();
const _aimPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _aimAt = new THREE.Vector3();
const _ndc0 = new THREE.Vector2(0, 0);
const aimPoint = (): THREE.Vector3 => {
  _aimRay.setFromCamera(_ndc0, camera);
  if (!_aimRay.ray.intersectPlane(_aimPlane, _aimAt)) {
    // looking at the sky: four metres ahead
    _aimAt.copy(_aimRay.ray.direction).setY(0).normalize().multiplyScalar(4).add(camera.position).setY(0);
  }
  return _aimAt.clone();
};
const interact = (): void => {
  _aimRay.setFromCamera(_ndc0, camera);
  const hits = _aimRay.intersectObjects(picker.pickables, true);
  if (hits.length) {
    let o: THREE.Object3D | null = hits[0].object;
    while (o && !o.userData.tapName) o = o.parent;
    if (o) {
      picker.onTapObject?.(o.userData.tapName as string, hits[0].object);
      return;
    }
  }
  picker.onTap?.(aimPoint());
};
let spaceDownAt = 0;
const keyFrame = (): void => {
  if (keys.took('KeyE')) interact();
  if (keys.took('KeyF')) setTreatMode(!treatMode);
  if (keys.took('KeyC')) heard('come here', true);
  if (keys.took('Digit1')) setToy('ball');
  if (keys.took('Digit2')) setToy('stick');
  if (keys.took('Digit3')) setToy('frisbee');
  if (keys.took('Digit4')) setToy('labubu');
  if (keys.took('Digit5')) setToy('off');
  if (keys.took('Space')) {
    if (toyOn || treatMode) {
      spaceDownAt = performance.now();
      charging = true;
      say('charging…', true);
    } else setToy(lastToy);
  }
  if (spaceDownAt && keys.held('Space')) {
    const s = (performance.now() - spaceDownAt) / 1000;
    const n = Math.min(8, Math.round(((s - 0.2) / 1.3) * 8));
    say('throw ' + '▮'.repeat(Math.max(1, n)) + '▯'.repeat(8 - Math.max(1, n)), true);
  }
  if (spaceDownAt && keys.let_go('Space')) {
    const s = (performance.now() - spaceDownAt) / 1000;
    spaceDownAt = 0;
    charging = false;
    if (toyOn || treatMode) {
      fetch.throw(camera.position, aimPoint(), Math.max(0.25, s));
      showToy();
      notePlay();
    }
  }
  keys.endFrame();
};

let qualityAccum = 0;
const clock = new THREE.Clock();
const camLook = new THREE.Vector3();
const _sunAt = new THREE.Vector3();
function frame(): void {
  const dt = Math.min(clock.getDelta(), 1 / 30); // clamp so tab-switches don't explode the springs
  if (motion.active) dog.setPhoneFlat(motion.attitude === 'flat');
  feeding.update(dt, camera.position);
  fetch.you.copy(camera.position);
  fetch.update(dt, camera.position);
  games.update(dt);
  treatFromHand();
  {
    const vp = visitors.position;
    if (vp && fetch.root.visible && fetch.state !== 'idle' && vp.distanceTo(fetch.root.position) < 0.45 && visitors.hitByToy()) {
      dog.sounds.play('thud', 0.5);
      household.care.record('play', { hunger: dog.hunger, bowlFill: bowl.fill });
    }
  }
  visitors.update(dt, camera.position);
  student.update(dt);
  agility.update(dt);
  coverTick(dt);
  care.update(dt, camera.position);
  business.raining = weather.sky === 'rain';
  business.update(dt);
  home.update(dt, weather.daylight < 0.25 || weather.sky === 'rain');
  // quality time: only while the app is visible and she's with you
  if (document.visibilityState === 'visible' && (license.mine || !license.holder)) {
    qualityAccum += dt;
    if (qualityAccum >= 30) {
      household.care.addQualityTime(license.device, qualityAccum);
      qualityAccum = 0;
      household.markDirty();
    }
  }
  care.dailyCheck(today(), household.care.quality);
  dressUp();
  if (togetherDays() !== lastDays) {
    lastDays = togetherDays();
    applyHoliday();
  }
  weather.update(dt, dog.root.position);
  // torch: from your hand toward her, brightening as it gets dark
  torch.position.copy(camera.position).y -= 0.15;
  torch.target.position.copy(dog.root.position).y += 0.35;
  torch.intensity = feeding.torch ? 60 * Math.max(0, 1 - weather.daylight * 2) : 0;
  yard.setNight(1 - weather.daylight);
  landmarks.setNight(Math.max(0, 1 - weather.daylight * 2));
  neighbourhood.setNight(Math.max(0, 1 - weather.daylight * 2));
  if (performance.now() > zachNext) zachRun();
  for (const p of people) p.update(dt, camera.position, fetch);
  skyDome.follow(camera.position);
  tricks.noTreatDecay();
  keyFrame();
  syncHeld();
  if (walker.on && treatMode) youPoint.copy(walker.pos);
  dog.update(dt, camera.position);
  void charging;
  // camera: follow the dog (eased) unless you've taken the camera; come back after 25 s idle
  if (!rig.follow && performance.now() - rig.lastInput > 25_000) {
    rig.follow = true;
    recenterBtn.hidden = true;
  }
  if (rig.follow) rig.target.lerp(dog.root.position, 1 - Math.exp(-3 * dt));
  else if (teleportTo) {
    rig.target.lerp(teleportTo, 1 - Math.exp(-5 * dt));
    rig.lastInput = performance.now(); // don't snap back mid-glide
    if (rig.target.distanceTo(teleportTo) < 0.05) teleportTo = null;
  }
  if (walker.on && !photoBusy) {
    walker.update(dt, keys.axis, keys.run, colliders, camera, camLook, keys.turn);
    rig.target.copy(walker.pos);
    rig.lastInput = performance.now(); // on foot the camera never snaps back to following
  } else if (!photoBusy) {
    camera.position.set(
      rig.target.x + Math.sin(rig.yaw) * Math.cos(rig.pitch) * rig.dist,
      rig.target.y + Math.sin(rig.pitch) * rig.dist,
      rig.target.z + Math.cos(rig.yaw) * Math.cos(rig.pitch) * rig.dist,
    );
    camLook.lerp(rig.target, 1 - Math.exp(-6 * dt));
    camera.lookAt(camLook.x, camLook.y + 0.4, camLook.z);
  } else {
    camLook.lerp(dog.root.position, 1 - Math.exp(-30 * dt));
    camera.lookAt(camLook.x, camLook.y + 0.45 * dog.profile.size, camLook.z);
  }
  // shadows: the 16 m shadow box travels with whoever the camera is on; the land is far bigger
  _sunAt.copy(walker.on ? walker.pos : rig.target);
  sun.position.set(_sunAt.x + 4, 8, _sunAt.z + 3);
  sun.target.position.copy(_sunAt);
  renderer.render(scene, camera);
  requestAnimationFrame(frame);
}
// debug handle for poking at the dog from the console / remote devtools (also on the phone)
(window as unknown as { lunaloo: unknown }).lunaloo = { dog, setBody, landmarks, student, agility, goPlay, account, camera, feeding, bowl, household, fetch, visitors, yard, tricks, care, photo, album, weather, business, license, rig, home, games, voice, keys, walker, startWalk, stopWalk, colliders, neighbourhood, people, zachRun, skyDome };

frame();
